import { useMemo } from "react";
import type { PublicQuote } from "@forgepoint/shared";
import { useQuoteDocumentContext } from "./quoteDocumentContext";
import { MarkdownProse } from "./MarkdownProse";
import { emptyLineFieldLabel, resolveLineField } from "./lineFields";
import { useLineScope, type LineScopeValue } from "./lineScopeContext";
import {
  FIELD_PLACEHOLDER_CHIP_CLASS,
  quoteFieldLabel,
  resolveQuoteField,
} from "./quoteFields";
import { remarkFieldTokens } from "./remarkFieldTokens";
import type { FpCommonProps } from "./types";

interface FpMarkdownProps extends FpCommonProps {
  /** The bound field key, or null for an authored (content-carrying) block. */
  source: string | null;
  /** The element's raw markdown text, used only when `source` is null. */
  content: string;
}

/**
 * Renders `<fp-markdown>`: a bound quote field, a line-scope field inside a
 * repeater (e.g. a NOTE line's `description`), or authored markdown content.
 * @param props - The optional field source, authored content, and common fp-* props
 * @returns The rendered markdown block, or a placeholder chip when unresolved
 */
export function FpMarkdown({
  source,
  content,
  className,
  style,
  "data-fp-node": dataFpNode,
}: FpMarkdownProps) {
  const { quote, editable } = useQuoteDocumentContext();
  const scope = useLineScope();
  const fieldTokenPlugins = useMemo(
    () => [
      remarkFieldTokens((tokenSource) => {
        const resolved = resolveQuoteField(quote, tokenSource);
        return resolved !== null && resolved !== ""
          ? {
              value: resolved,
              title: editable
                ? (quoteFieldLabel(tokenSource) ?? undefined)
                : undefined,
            }
          : {
              placeholderLabel: quoteFieldLabel(tokenSource) ?? "Empty field",
            };
      }),
    ],
    [quote, editable],
  );
  const value =
    source === null
      ? content
      : (resolveLineMarkdown(quote, scope, source) ??
        resolveQuoteField(quote, source));
  if (value === "" && source !== null && !editable) {
    return null;
  }
  if (value === null || (value === "" && source !== null)) {
    return (
      <span className={FIELD_PLACEHOLDER_CHIP_CLASS} data-fp-node={dataFpNode}>
        {`⟨${placeholderLabel(quote, scope, source)}⟩`}
      </span>
    );
  }
  return (
    <MarkdownProse
      markdown={value}
      extraRemarkPlugins={fieldTokenPlugins}
      className={className}
      style={style}
      data-fp-node={dataFpNode}
    />
  );
}

/**
 * The chip label for an unresolved binding: the row-type-aware line label
 * inside a repeater, the quote field's label otherwise.
 * @param quote - The public quote backing the document
 * @param scope - The enclosing repeater scope, or null at quote scope
 * @param source - The bound field key, or null when unset
 * @returns The label to show inside the chip
 */
function placeholderLabel(
  quote: PublicQuote,
  scope: LineScopeValue | null,
  source: string | null,
): string {
  if (source === null) {
    return "no source";
  }
  if (scope && resolveLineField(quote, scope, source) !== null) {
    return emptyLineFieldLabel(source, scope.line.type);
  }
  return quoteFieldLabel(source) ?? source;
}

/**
 * Resolves a source against the enclosing repeater scope, or null when the
 * element sits outside a repeater or the source is not a text line field.
 * @param quote - The public quote backing the document
 * @param scope - The enclosing repeater scope, or null at quote scope
 * @param source - The bound field key
 * @returns The line-scope markdown text, or null to fall back to quote scope
 */
function resolveLineMarkdown(
  quote: PublicQuote,
  scope: LineScopeValue | null,
  source: string,
): string | null {
  if (!scope) {
    return null;
  }
  const resolved = resolveLineField(quote, scope, source);
  return resolved !== null && resolved.format === "text"
    ? resolved.value
    : null;
}
